/**
 * Accounts Page
 * 
 * List and manage bank accounts.
 * 
 * @validates Requirements 4.1, 4.2, 4.3 - Account management
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAccountStore } from '../store/accountStore';
import { useAuthStore } from '../store/authStore';
import { Card, Button } from '../components/ui';
import { BankIcon } from '../components/ui/BankIcon';
import { formatCurrency, formatDate } from '../utils/formatters';

const AccountsPage = () => {
  const { t } = useTranslation();
  const { isPremium } = useAuthStore();
  const { accounts, balances, fetchAccounts, deleteAccount, isLoading, error } = useAccountStore();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleDelete = async (id: string) => {
    if (window.confirm(t('accounts.deleteConfirm'))) {
      await deleteAccount(id);
      setDeleteId(null);
    }
  };

  // Check if user has any owned accounts (not just shared)
  const hasOwnedAccounts = accounts.some(acc => !acc.isShared);
  const hasOnlySharedAccounts = accounts.length > 0 && !hasOwnedAccounts;
  
  // Can add account: premium user, or free user with no owned accounts (shared don't count)
  const ownedAccountsCount = accounts.filter(acc => !acc.isShared).length;
  const canAddAccount = isPremium() || ownedAccountsCount === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('accounts.title')}</h1>
          <p className="text-slate-500 dark:text-slate-400">{t('accounts.subtitle')}</p>
        </div>
        {/* Hide add button for users with only shared accounts */}
        {!hasOnlySharedAccounts && (
          canAddAccount ? (
            <Link to="/accounts/new">
              <Button>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline ml-1">{t('accounts.add')}</span>
              </Button>
            </Link>
          ) : (
            <Link to="/premium">
              <Button variant="secondary">{t('accounts.upgradeToPremium')}</Button>
            </Link>
          )
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Account limit warning for free users (only if they have owned accounts) */}
      {!isPremium() && !hasOnlySharedAccounts && ownedAccountsCount >= 1 && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">{t('accounts.limitReached.title')}</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">{t('accounts.limitReached.message')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Accounts List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-slate-100 dark:bg-slate-700 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('accounts.empty.title')}</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">{t('accounts.empty.message')}</p>
            <Link to="/accounts/new">
              <Button size="lg">{t('accounts.empty.create')}</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => {
            const balance = balances[account.id];
            const canEdit = !account.isShared || account.permission === 'write' || account.permission === 'owner';
            const canDelete = !account.isShared; // Only owner can delete
            return (
              <Card key={account.id} className="hover:shadow-md transition-shadow">
                {/* Desktop layout */}
                <div className="hidden md:flex items-center gap-4">
                  <BankIcon 
                    bankName={account.bankName || account.name} 
                    size="lg"
                    className="flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{account.name}</h3>
                          {account.isShared && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                              {account.permission === 'write' 
                                ? t('accounts.sharedWrite', 'Partagé (écriture)')
                                : t('accounts.sharedRead', 'Partagé (lecture)')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{account.bankName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                          {formatCurrency(account.balance ?? 0, account.currency)}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {t('accounts.pointed')}: {formatCurrency(balance?.balance ?? account.balance ?? 0, account.currency)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                      {account.iban && (
                        <span className="font-mono">{account.iban.replace(/(.{4})/g, '$1 ').trim()}</span>
                      )}
                      {account.openingDate && (
                        <span>{t('accounts.openedOn')} {formatDate(account.openingDate)}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Link to={`/accounts/${account.id}/operations`}>
                      <Button variant="ghost" size="sm">{t('accounts.operations')}</Button>
                    </Link>
                    {canEdit && (
                      <Link to={`/accounts/${account.id}/edit`}>
                        <Button variant="outline" size="sm">{t('common.edit')}</Button>
                      </Link>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(account.id)}
                        disabled={deleteId === account.id}
                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Mobile layout - optimized with amounts on right */}
                <div className="md:hidden space-y-3">
                  {/* Row 1: Bank icon + Account name + Balance */}
                  <div className="flex items-start gap-3">
                    <BankIcon 
                      bankName={account.bankName || account.name} 
                      size="md"
                      className="flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{account.name}</h3>
                          {account.isShared && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                              {account.permission === 'write' 
                                ? t('accounts.sharedWrite', 'Partagé (écriture)')
                                : t('accounts.sharedRead', 'Partagé (lecture)')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{account.bankName}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-slate-900 dark:text-slate-100">
                          {formatCurrency(account.balance ?? 0, account.currency)}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {t('accounts.pointed')}: {formatCurrency(balance?.balance ?? account.balance ?? 0, account.currency)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: IBAN + Date */}
                  <div className="text-slate-500 dark:text-slate-400 space-y-1">
                    {account.iban && (
                      <p className="font-mono text-[10px]">{account.iban.replace(/(.{4})/g, '$1 ').trim()}</p>
                    )}
                    {account.openingDate && (
                      <p className="text-xs">{t('accounts.openedOn')} {formatDate(account.openingDate)}</p>
                    )}
                  </div>

                  {/* Row 3: Action buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <Link to={`/accounts/${account.id}/operations`}>
                      <Button variant="primary" size="sm">{t('accounts.operations')}</Button>
                    </Link>
                    {canEdit && (
                      <Link to={`/accounts/${account.id}/edit`}>
                        <Button variant="outline" size="sm">{t('common.edit')}</Button>
                      </Link>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(account.id)}
                        disabled={deleteId === account.id}
                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountsPage;
